import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef } from 'react'
import { isShapeGeometryGestureBlocked } from '@/lib/groups/element-edit-policy'
import { resolveAltClickCycleStep } from '../helpers/alt-click-cycle-step'
import { sortTopmostFirst } from '../helpers/paint-order'
import { isShapeSelectable } from '../helpers/selection-scope'
import type { AltClickCycleStep } from '../types/alt-click-cycle-step'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { EditScopeRefsApi } from './use-edit-scope-refs'
import type { SelectionHistoryApi } from './use-selection-history'
import type { SelectionPresentationApi } from './use-selection-presentation'
import type { FeedbackPulsesApi } from './use-feedback-pulses'
import type { GestureStartApi } from './use-gesture-start'
import type { ShapeHitTestingApi } from './use-shape-hit-testing'

/**
 * Alt+Click cycles through the objects stacked under the pointer, and Shift+Alt+Click removes the
 * multi-selection highlight (WMPCM-12824). Inside an isolated group it steps through every member
 * of the group instead (WMPCM-13465). Every pointer-down route (a shape body, single-select
 * chrome, multi-select chrome) calls these, so the rules hold wherever the click lands. The cycle
 * rules themselves are in `resolveAltClickCycleStep`.
 *
 * Alt+drag duplicates the selection instead, so a press only starts a move here. A step that
 * changes what a drag would carry (the first pick at a stack) applies at once; any later step waits
 * for pointer-up and runs only when the press never became a drag, so Alt+dragging the object you
 * cycled to copies that object rather than the next one.
 */
export function useAltClickCycle(
  ctx: ShapeManagerStateApi &
    EditScopeRefsApi &
    SelectionHistoryApi &
    SelectionPresentationApi &
    FeedbackPulsesApi &
    GestureStartApi &
    ShapeHitTestingApi
) {
  const {
    altHoveredShapeId,
    ctrlCycleRef,
    editScopeGroupIdRef,
    elementZIndexMap,
    getLocalPointFromEvent,
    getStackAtPoint,
    handleMoveStart,
    interactionRef,
    onShapeClick,
    recordShapeSelectionHistory,
    selectedShapeIds,
    selectedShapeIdsRef,
    setAltHoveredShapeId,
    setSelectedShapeIds,
    shapesById,
    shapesRef,
    triggerFrozenGeometryBlockedPulse,
  } = ctx

  // The shape the last Alt+Click picked, so the next one at the same stack moves on from it.
  const lastCycledIdRef = useRef<string | null>(null)

  // Any other click ends the cycle, so the next Alt+Click starts from the point clicked again.
  // Clicking the object the cycle reached leaves the selection as it is, so the click handlers call
  // this on every press without Alt.
  const resetAltClickCycle = useCallback(() => {
    lastCycledIdRef.current = null
  }, [])

  // A selection the cycle didn't make (marquee, Select All, undo, a shortcut) ends it too. The only
  // selection an Alt+Click makes is its own pick on its own.
  useEffect(() => {
    const lastCycledId = lastCycledIdRef.current

    if (lastCycledId == null) return

    const isCyclePick = selectedShapeIds.length === 1 && selectedShapeIds[0] === lastCycledId
    const isCycleHighlight = selectedShapeIds.length >= 2 && selectedShapeIds.includes(lastCycledId)

    if (!isCyclePick && !isCycleHighlight) {
      lastCycledIdRef.current = null
    }
  }, [selectedShapeIds])

  const applyStep = useCallback(
    (step: AltClickCycleStep) => {
      lastCycledIdRef.current = step.shapeId

      if (step.kind === 'highlight') {
        setAltHoveredShapeId(step.shapeId)
        return
      }

      // Inside an isolated group the pick is highlighted too, so Shift+Alt+Click has one to remove.
      if (editScopeGroupIdRef.current) {
        setAltHoveredShapeId(step.shapeId)
      }

      const before = [...selectedShapeIdsRef.current]

      if (before.length === 1 && before[0] === step.shapeId) return

      const next = [step.shapeId]

      selectedShapeIdsRef.current = next
      setSelectedShapeIds(next)
      recordShapeSelectionHistory(before, next)
      onShapeClick?.(step.shapeId)
    },
    [
      editScopeGroupIdRef,
      onShapeClick,
      recordShapeSelectionHistory,
      selectedShapeIdsRef,
      setAltHoveredShapeId,
      setSelectedShapeIds,
    ]
  )

  const handleAltClick = useCallback(
    (event: ReactPointerEvent) => {
      ctrlCycleRef.current = null

      const point = getLocalPointFromEvent(event)
      const selectedIds = selectedShapeIdsRef.current
      const isolatedGroupId = editScopeGroupIdRef.current
      const step = resolveAltClickCycleStep({
        stackIds: point ? getStackAtPoint(point).map((shape) => shape.id) : [],
        // The same paint order `getStackAtPoint` uses.
        selectedTopFirst: sortTopmostFirst(
          shapesRef.current.filter((shape) => selectedIds.includes(shape.id)),
          shapesRef.current,
          elementZIndexMap
        ).map((shape) => shape.id),
        highlightedId: altHoveredShapeId,
        lastCycledId: lastCycledIdRef.current,
        memberTopFirst: isolatedGroupId
          ? sortTopmostFirst(
              shapesRef.current.filter((shape) => isShapeSelectable(shape, isolatedGroupId)),
              shapesRef.current,
              elementZIndexMap
            ).map((shape) => shape.id)
          : undefined,
      })

      if (step?.kind === 'select' && !step.isContinuing) {
        const target = shapesById.get(step.shapeId)
        const wasAlreadySelected = selectedShapeIdsRef.current.includes(step.shapeId)

        applyStep(step)

        // Same as a plain click: a locked or frozen object is selected but not moved.
        if (target && isShapeGeometryGestureBlocked(target, shapesById)) {
          if (wasAlreadySelected) {
            triggerFrozenGeometryBlockedPulse(target)
          }

          return
        }

        // The selection state above has not rendered yet, so name the shape the move drives.
        handleMoveStart(event, step.shapeId, true)
        return
      }

      handleMoveStart(event)

      if (!step) return

      const interaction = interactionRef.current

      if (interaction?.pointerId === event.pointerId) {
        interactionRef.current = { ...interaction, onClickRelease: () => applyStep(step) }
      } else {
        // No move started (the selection can't be moved), so this press can only be a click.
        applyStep(step)
      }
    },
    [
      altHoveredShapeId,
      applyStep,
      ctrlCycleRef,
      editScopeGroupIdRef,
      elementZIndexMap,
      getLocalPointFromEvent,
      getStackAtPoint,
      handleMoveStart,
      interactionRef,
      selectedShapeIdsRef,
      shapesById,
      shapesRef,
      triggerFrozenGeometryBlockedPulse,
    ]
  )

  // Removes the highlight when the highlighted object is anywhere under the pointer, even beneath
  // another selected object. The selection itself is left alone.
  const handleShiftAltClick = useCallback(
    (event: ReactPointerEvent) => {
      if (!altHoveredShapeId) return

      const point = getLocalPointFromEvent(event)
      const isHighlightUnderPointer =
        point != null && getStackAtPoint(point).some((shape) => shape.id === altHoveredShapeId)

      if (isHighlightUnderPointer) {
        lastCycledIdRef.current = null
        setAltHoveredShapeId(null)
      }
    },
    [altHoveredShapeId, getLocalPointFromEvent, getStackAtPoint, setAltHoveredShapeId]
  )

  return { handleAltClick, handleShiftAltClick, resetAltClickCycle }
}

export type AltClickCycleApi = ReturnType<typeof useAltClickCycle>
